import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export default function Archive() {
  const [folders, setFolders] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    api.get("/folders").then((r) => setFolders(r.data));
  }, []);

  const filtered = useMemo(() => {
    return folders.filter((f) => {
      if (statusFilter !== "ALL" && f.status !== statusFilter) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        f.id.toLowerCase().includes(q) ||
        (f.title || "").toLowerCase().includes(q) ||
        (f.body || "").toLowerCase().includes(q)
      );
    });
  }, [folders, query, statusFilter]);

  // Group by boxes of 10 (Box 00: A-000..A-009, Box 01: A-010..A-019, ..., Box 10: A-100)
  const boxes = chunk(filtered, 10);

  return (
    <div data-testid="archive-page" className="space-y-12">
      <header className="space-y-6">
        <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
          Section II · Folder Archive
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter leading-none">
          The Archive
        </h1>
        <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
          One hundred and one folders. Eleven storage crates. Open with care — some seals were broken
          before they reached us.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center pt-4 border-t border-[#333]">
          <input
            className="tiv-input sm:max-w-sm"
            placeholder="search by id, title, contents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="archive-search-input"
          />
          <div className="flex gap-2">
            {["ALL", "ACTIVE", "REDACTED", "LOST"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`tiv-btn ${statusFilter === s ? "bg-[#E3E1D9] text-[#050505]" : ""}`}
                data-testid={`archive-filter-${s.toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="text-[0.65rem] tracking-[0.3em] uppercase text-[#555] sm:ml-auto">
            {filtered.length} / {folders.length} folders
          </div>
        </div>
      </header>

      <div className="space-y-16">
        {boxes.map((box, idx) => {
          const first = box[0]?.id || "—";
          const last = box[box.length - 1]?.id || "—";
          const boxNum = String(idx).padStart(2, "0");
          return (
            <section key={boxNum} data-testid={`archive-box-${boxNum}`}>
              <div className="section-label">
                <span>
                  Crate {boxNum} <span className="text-[#555] ml-3">{first} → {last}</span>
                </span>
                <span>{box.length} files</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-[#333]">
                {box.map((f) => (
                  <Link
                    key={f.id}
                    to={`/archive/${f.id}`}
                    className="folder-cell"
                    data-testid={`folder-cell-${f.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="folder-cell-id">{f.id}</div>
                      <div
                        className={`status-pill ${
                          f.status === "ACTIVE"
                            ? "status-active"
                            : f.status === "REDACTED"
                            ? "status-redacted"
                            : "status-lost"
                        }`}
                      >
                        {f.status}
                      </div>
                    </div>
                    <div className="folder-cell-title line-clamp-2">
                      {f.title || <span className="text-[#555] italic">[ untitled ]</span>}
                    </div>
                    <div className="folder-cell-meta mt-auto">
                      {f.classification} {f.date && `· ${f.date}`}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
        {boxes.length === 0 && (
          <div className="text-[#555] text-xs tracking-widest uppercase border border-[#333] p-10 text-center">
            ::: no folders match the present sigil :::
          </div>
        )}
      </div>
    </div>
  );
}
